import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';
import { StreamChat } from 'stream-chat';
import {
  Chat,
  Channel,
  ChannelHeader,
  ChannelList,
  MessageList,
  MessageInput,
  Thread,
  Window,
  useChannelStateContext,
  useChatContext,
  useMessageContext,
  MessageSimple,
} from 'stream-chat-react';
import { EmojiPicker } from 'stream-chat-react/emojis';
import 'stream-chat-react/dist/css/v2/index.css';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, MessageSquare, Plus, Users, Search, X, Hash, ArrowLeft, Mic, Square, Trash2, Pencil, Video, BellOff, Bell, Clock } from 'lucide-react';
import { findBibleReferences } from '../utils/bibleReferences';
import { useBibleRefViewer } from './BibleRefViewer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const STREAM_KEY = process.env.REACT_APP_STREAM_CHAT_API_KEY;

// =========================================================================
// OOO Context — set de Stream user IDs que están fuera de oficina ahora.
// Refrescado cada 60s. Combinado con la presencia nativa de Stream Chat
// para dibujar dots verde (online) o rojo (OOO) en cada avatar de DM.
// =========================================================================
const OOOContext = React.createContext(new Set());

const OOOProvider = ({ children }) => {
  const [oooSet, setOooSet] = useState(new Set());
  useEffect(() => {
    let cancel = false;
    let timer = null;
    const fetchOnce = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/stream-chat/ooo-list`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (cancel) return;
        setOooSet(new Set(res.data?.ooo_user_ids || []));
      } catch (_e) {
        // silencioso — sin dot rojo si falla
      }
    };
    fetchOnce();
    timer = setInterval(fetchOnce, 60000);
    return () => { cancel = true; if (timer) clearInterval(timer); };
  }, []);
  return <OOOContext.Provider value={oooSet}>{children}</OOOContext.Provider>;
};

// Audio Recording Button for MessageInput
const AudioRecordButton = () => {
  const { channel } = useChannelStateContext();
  const [recording, setRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunks.current = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };
      
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks.current, { type: 'audio/webm' });
        const file = new File([blob], `audio_${Date.now()}.webm`, { type: 'audio/webm' });
        
        try {
          const response = await channel.sendFile(file);
          await channel.sendMessage({
            text: '',
            attachments: [{
              type: 'audio',
              asset_url: response.file,
              title: `Mensaje de voz (${duration}s)`,
              mime_type: 'audio/webm',
              file_size: blob.size,
            }]
          });
          toast.success('Audio enviado');
        } catch (err) {
          toast.error('Error enviando audio');
        }
      };
      
      mediaRecorder.current = recorder;
      recorder.start();
      setRecording(true);
      setDuration(0);
      timer.current = setInterval(() => setDuration(d => d + 1), 1000);
    } catch (err) {
      toast.error('No se pudo acceder al micrófono');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.stop();
    }
    clearInterval(timer.current);
    setRecording(false);
  };

  return recording ? (
    <button
      onClick={stopRecording}
      className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white rounded-full text-xs font-medium animate-pulse"
      data-testid="stop-recording-btn"
    >
      <Square className="w-3 h-3 fill-current" />
      {duration}s
    </button>
  ) : (
    <button
      onClick={startRecording}
      className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
      title="Grabar audio"
      data-testid="record-audio-btn"
    >
      <Mic className="w-5 h-5 text-gray-500" />
    </button>
  );
};

// Deep-link a un canal vía query params: ?channelId=xxx&channelType=yyy
// Cuando el usuario toca una notificación push, se abre el canal directo.
const ChannelDeepLink = () => {
  const { client, setActiveChannel } = useChatContext();
  React.useEffect(() => {
    if (!client?.userID) return;
    const params = new URLSearchParams(window.location.search);
    const channelId = params.get('channelId');
    const channelType = params.get('channelType') || 'messaging';
    if (!channelId) return;
    (async () => {
      try {
        const ch = client.channel(channelType, channelId);
        await ch.watch();
        setActiveChannel(ch);
        // Limpiar query params para evitar reabrir si el user navega
        const url = new URL(window.location.href);
        url.searchParams.delete('channelId');
        url.searchParams.delete('channelType');
        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        console.warn('No se pudo abrir canal por deep-link:', e?.message);
      }
    })();
  }, [client?.userID]); // eslint-disable-line
  return null;
};

// Custom MessageInput with audio button + (admin/profe) custom Poll button
// Enter = nueva línea, Shift+Enter = enviar mensaje
// IMPORTANTE: Ocultamos el botón nativo de Encuesta de Stream (su modal se cortaba en mobile)
// y proveemos uno propio que abre un modal estilo Red Social. Llama channel.createPoll().
const CustomMessageInput = ({ canCreatePolls = true }) => {
  const shouldSubmit = (event) => event.key === 'Enter' && event.shiftKey;
  const { channel } = useChannelStateContext();
  const { client } = useChatContext();
  const [showPollModal, setShowPollModal] = useState(false);

  const handleCreateChatPoll = async ({ question, options, closesAt, showVoters }) => {
    try {
      // Stream Chat poll creation API
      const poll = await client.createPoll({
        name: question,
        description: '',
        options: options.map(text => ({ text })),
        enforce_unique_vote: true,
        max_votes_allowed: 1,
        allow_user_suggested_options: false,
        voting_visibility: showVoters ? 'public' : 'anonymous',
      });
      // Adjuntar el poll como mensaje en el canal
      await channel.sendMessage({
        text: `📊 ${question}`,
        poll_id: poll.poll.id,
      });
      // Si tiene fecha de cierre, programar el close
      if (closesAt) {
        const closeMs = new Date(closesAt).getTime() - Date.now();
        if (closeMs > 0 && closeMs < 7 * 24 * 60 * 60 * 1000) { // máx 7 días
          setTimeout(() => {
            client.updatePoll({ id: poll.poll.id, name: question, is_closed: true }).catch(() => {});
          }, closeMs);
        }
      }
      toast.success('📊 Encuesta publicada');
    } catch (e) {
      console.error('Error creando poll en chat:', e);
      toast.error('No se pudo crear la encuesta');
      throw e;
    }
  };

  return (
    <div
      className="flex items-center gap-1 w-full iba-no-polls"
      data-testid="custom-message-input"
    >
      <div className="flex-1">
        <MessageInput shouldSubmit={shouldSubmit} grow maxRows={10} />
      </div>
      {canCreatePolls && (
        <button
          type="button"
          onClick={() => setShowPollModal(true)}
          className="p-2 rounded-full hover:bg-purple-50 text-purple-600 transition-colors shrink-0"
          title="Crear encuesta"
          data-testid="chat-create-poll-btn"
        >
          <i className="fas fa-poll text-base" />
        </button>
      )}
      <AudioRecordButton />
      {canCreatePolls && (
        <ChatPollModal
          isOpen={showPollModal}
          onClose={() => setShowPollModal(false)}
          onSubmit={handleCreateChatPoll}
        />
      )}
    </div>
  );
};

// Modal de creación de encuesta para Stream Chat (clon visual del de Red Social).
// No depende del DOM nativo del SDK — render Tailwind 100% controlado, mobile-friendly.
const ChatPollModal = ({ isOpen, onClose, onSubmit }) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [closesAt, setClosesAt] = useState('');
  const [showVoters, setShowVoters] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setQuestion(''); setOptions(['', '']); setClosesAt(''); setShowVoters(false); };
  const updateOpt = (i, v) => setOptions(p => p.map((o, idx) => idx === i ? v : o));
  const addOpt = () => { if (options.length < 6) setOptions(p => [...p, '']); };
  const removeOpt = (i) => { if (options.length > 2) setOptions(p => p.filter((_, idx) => idx !== i)); };

  const submit = async () => {
    const clean = options.map(o => o.trim()).filter(Boolean);
    if (question.trim().length < 3) { toast.error('Pregunta muy corta'); return; }
    if (clean.length < 2) { toast.error('Mínimo 2 opciones'); return; }
    setSubmitting(true);
    try {
      await onSubmit({
        question: question.trim(),
        options: clean,
        closesAt: closesAt || null,
        showVoters,
      });
      reset();
      onClose();
    } catch (e) { /* manejado en handler */ }
    finally { setSubmitting(false); }
  };

  if (!isOpen) return null;
  return createPortal(
    <div
      className="iba-chat-poll-modal-backdrop"
      data-testid="chat-poll-modal"
      onClick={(e) => { if (e.target === e.currentTarget) { reset(); onClose(); } }}
    >
      <div className="iba-chat-poll-modal-card">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <i className="fas fa-poll text-purple-600" /> Crear encuesta
          </h3>
          <button onClick={() => { reset(); onClose(); }} className="p-1 hover:bg-gray-100 rounded-full">
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Pregunta</label>
            <textarea
              value={question}
              onChange={e => setQuestion(e.target.value)}
              maxLength={280}
              placeholder="¿Qué tema querés tratar?"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none h-20 focus:ring-2 focus:ring-purple-500"
              data-testid="chat-poll-question"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Opciones (2-6)</label>
            <div className="space-y-2">
              {options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 w-5">{i + 1}.</span>
                  <input
                    type="text" value={opt}
                    onChange={e => updateOpt(i, e.target.value)}
                    maxLength={120} placeholder={`Opción ${i + 1}`}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-500"
                    data-testid={`chat-poll-opt-${i}`}
                  />
                  {options.length > 2 && (
                    <button onClick={() => removeOpt(i)} className="text-gray-400 hover:text-red-500 p-1">
                      <i className="fas fa-times" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {options.length < 6 && (
              <button onClick={addOpt} className="mt-2 text-xs text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1">
                <i className="fas fa-plus" /> Agregar opción
              </button>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Fecha de cierre (opcional, máx 7 días)</label>
            <input
              type="datetime-local" value={closesAt}
              onChange={e => setClosesAt(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={showVoters} onChange={e => setShowVoters(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-purple-600 cursor-pointer" />
            <div className="text-sm text-gray-700">
              <span className="font-medium">Mostrar quién votó qué</span>
              <div className="text-xs text-gray-500">Si está apagado, los votos son anónimos</div>
            </div>
          </label>
        </div>
        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex justify-end gap-2">
          <button onClick={() => { reset(); onClose(); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
          <button onClick={submit} disabled={submitting || question.trim().length < 3}
            className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white rounded-lg flex items-center gap-2"
            data-testid="chat-poll-submit">
            {submitting ? <><i className="fas fa-spinner fa-spin" /> Publicando...</> : 'Publicar encuesta'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Module-level dedup set for Genesis AI message processing
const _processedAIMessageIds = new Set();

// Helper: genera una URL de Daily.co con el token personal del usuario actual
// Llama al backend para que Daily.co no pida el nombre al unirse.
const buildCallUrl = async (url, _userName) => {
  if (!url) return url;
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
    const resp = await fetch(`${origin}/api/chat/video-join-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ room_url: url })
    });
    if (!resp.ok) return url;
    const data = await resp.json();
    return data.url || url;
  } catch (_e) {
    return url;
  }
};

// Detecta si el dispositivo es móvil (helper disponible por si hace falta en el futuro)
// eslint-disable-next-line no-unused-vars
const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /android|iphone|ipad|ipod|mobile|iemobile|blackberry/i.test(navigator.userAgent || '');
};

// Custom Message: renderiza una card especial para mensajes de videollamada
const CustomMessage = (props) => {
  const { message } = useMessageContext();
  const bibleViewer = useBibleRefViewer();
  // Los campos custom pueden llegar top-level (JS SDK) o dentro de message.custom (Python SDK / backend)
  const custom = message?.custom || {};
  const mVideoCall = message?.video_call ?? custom.video_call;
  const mVideoCallUrl = message?.video_call_url ?? custom.video_call_url;
  const mVideoCallCaller = message?.video_call_caller ?? custom.video_call_caller;
  const mVideoCallStartedAt = message?.video_call_started_at ?? custom.video_call_started_at;
  const mVideoCallEnded = message?.video_call_ended ?? custom.video_call_ended;
  const mIsPtt = message?.is_ptt ?? custom.is_ptt ?? message?.ptt_call ?? custom.ptt_call;
  const mVoiceNote = message?.voice_note ?? custom.voice_note;
  const mVoiceNoteUrl = message?.voice_note_url ?? custom.voice_note_url;
  const mVoiceNoteSender = message?.voice_note_sender ?? custom.voice_note_sender;

  // Mensaje compacto de "Llamada finalizada"
  if (mVideoCallEnded) {
    return (
      <div className="flex justify-center my-2" data-testid="call-ended-message">
        <div className="px-3 py-1 rounded-full bg-gray-200 text-gray-600 text-xs flex items-center gap-1.5">
          <i className="fas fa-phone-slash" />
          <span>Llamada finalizada</span>
        </div>
      </div>
    );
  }
  if (mVideoCall && mVideoCallUrl) {
    const ownMsg = message.user?.id === (props.client?.userID || '');
    const caller = mVideoCallCaller || message.user?.name || 'Alguien';
    const url = mVideoCallUrl;
    const userName = props.client?.user?.name || message.user?.name || 'Invitado';
    const startedAt = mVideoCallStartedAt || (message.created_at ? new Date(message.created_at).getTime() : 0);
    const ageMin = startedAt ? Math.floor((Date.now() - startedAt) / 60000) : null;
    const isPtt = !!mIsPtt;
    return (
      <div className={`str-chat__message str-chat__message-simple ${ownMsg ? 'str-chat__message--me' : ''} my-2`} data-testid={isPtt ? 'ptt-call-message' : 'video-call-message'}>
        <div className={`flex ${ownMsg ? 'justify-end' : 'justify-start'} w-full`}>
          <div className={`rounded-2xl overflow-hidden shadow-lg max-w-xs ${isPtt ? 'bg-gradient-to-br from-orange-600 via-amber-500 to-yellow-500' : 'bg-gradient-to-br from-emerald-500 via-green-500 to-teal-500'}`}>
            <div className="p-4 text-white">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-11 h-11 bg-white/20 rounded-full flex items-center justify-center shrink-0">
                  {isPtt ? <i className="fas fa-broadcast-tower text-lg" /> : <Video className="w-6 h-6" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-base truncate">{isPtt ? 'Radio en vivo' : 'Videollamada'}</p>
                  <p className="text-xs opacity-90 truncate">
                    {ownMsg ? (isPtt ? 'Iniciaste Radio' : 'Iniciaste una llamada') : `${caller} llamó`}
                    {ageMin != null && ageMin >= 0 && (
                      <> · {ageMin === 0 ? 'ahora' : `hace ${ageMin} min`}</>
                    )}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  const eventName = isPtt ? 'iba:open-ptt-call' : 'iba:open-video-call';
                  window.dispatchEvent(new CustomEvent(eventName, {
                    detail: { url, channelId: message.cid, userName, isPtt, title: isPtt ? `Radio · ${caller}` : undefined }
                  }));
                }}
                className={`w-full py-2.5 rounded-xl font-bold text-sm transition-all active:scale-95 shadow-md ${isPtt ? 'bg-white text-orange-700 hover:bg-orange-50' : 'bg-white text-green-700 hover:bg-green-50'}`}
                data-testid="join-call-btn"
              >
                <i className={`fas ${isPtt ? 'fa-broadcast-tower' : 'fa-video'} mr-2`} />
                {isPtt ? 'Unirse a la Radio' : 'Unirse a la llamada'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // Nota de voz (broadcast PTT)
  if (mVoiceNote && mVoiceNoteUrl) {
    const ownMsg = message.user?.id === (props.client?.userID || '');
    const sender = mVoiceNoteSender || message.user?.name || 'Alguien';
    // El endpoint requiere auth — pasamos el token via ?token= porque <audio> no envía Bearer header
    const rawUrl = String(mVoiceNoteUrl).startsWith('http') ? mVoiceNoteUrl : `${window.location.origin}${mVoiceNoteUrl}`;
    const tk = (typeof localStorage !== 'undefined') ? (localStorage.getItem('token') || '') : '';
    const url = tk ? `${rawUrl}${rawUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(tk)}` : rawUrl;
    return (
      <div className={`str-chat__message str-chat__message-simple ${ownMsg ? 'str-chat__message--me' : ''} my-2`} data-testid="voice-note-message">
        <div className={`flex ${ownMsg ? 'justify-end' : 'justify-start'} w-full`}>
          <div className="rounded-2xl overflow-hidden shadow-lg max-w-xs bg-gradient-to-br from-orange-600 via-amber-500 to-yellow-500">
            <div className="p-4 text-white">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-11 h-11 bg-white/20 rounded-full flex items-center justify-center shrink-0">
                  <i className="fas fa-microphone text-lg" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">Nota de voz</p>
                  <p className="text-xs opacity-90 truncate">{ownMsg ? 'Enviaste una nota' : `De ${sender}`}</p>
                </div>
              </div>
              <audio
                src={url}
                controls
                preload="metadata"
                className="w-full rounded-lg"
                style={{ height: 40 }}
                data-testid="voice-note-audio"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
  // Default Stream message, con chip de referencias bíblicas detectadas debajo si las hay
  const bibleRefs = message?.text ? findBibleReferences(message.text) : [];
  if (bibleRefs.length > 0) {
    // Deduplicar por USFM
    const uniqueRefs = [];
    const seen = new Set();
    for (const r of bibleRefs) {
      if (!seen.has(r.usfm)) { seen.add(r.usfm); uniqueRefs.push(r); }
    }
    return (
      <>
        <MessageSimple {...props} />
        <div className="flex flex-wrap gap-1.5 px-3 pb-2 -mt-1" data-testid="chat-bible-refs">
          {uniqueRefs.map((r, idx) => (
            <button
              key={idx}
              type="button"
              onClick={(e) => { e.stopPropagation(); bibleViewer.open(r.usfm, r.match); }}
              className="inline-flex items-center gap-1 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full text-[11px] font-medium transition"
              title={`Ver ${r.match}`}
              data-testid="chat-bible-ref-chip"
            >
              <i className="fas fa-cross text-[9px]" />
              {r.match}
            </button>
          ))}
        </div>
      </>
    );
  }
  return <MessageSimple {...props} />;
};

const StreamChatComponent = () => {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [activeTab, setActiveTab] = useState('direct');
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [oooEnabled, setOooEnabled] = useState(false);
  const [oooAutoActive, setOooAutoActive] = useState(false); // OOO automático por horario (sin tocar el manual)
  const [oooOverrideActive, setOooOverrideActive] = useState(false); // Usuario anuló el auto manualmente
  const [oooMessage, setOooMessage] = useState('');
  const [oooAutoReplyAI, setOooAutoReplyAI] = useState(false);
  const [showOooPanel, setShowOooPanel] = useState(false);
  const [oooReturnModal, setOooReturnModal] = useState(null); // {pending_senders: [...], notifyChecked: bool}
  const [showSchedulePanel, setShowSchedulePanel] = useState(false);
  const [schedule, setSchedule] = useState({});
  const [savingSchedule, setSavingSchedule] = useState(false);

  // Llamada entrante
  const [incomingCall, setIncomingCall] = useState(null); // {url, caller, channelId, at}

  // Llamada activa (iframe global para móvil y receptores de llamada)
  const [activeCall, setActiveCall] = useState(null); // {url, channelId, title}

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Llamadas entrantes: ahora se manejan globalmente en <ChatIncomingCall />
  // (montado en Layout.js) para que el popup aparezca en CUALQUIER página
  // y para canales/grupos en los que el usuario es miembro pero no está
  // mirando activamente. Aquí dejamos solo el state vacío para no romper
  // referencias existentes.

  // Listener global para abrir el iframe de videollamada (usado por CustomMessage join button)
  useEffect(() => {
    if (!client) return;
    const handler = async (ev) => {
      const { url, channelId, userName, title } = ev.detail || {};
      if (!url) return;
      const finalUrl = await buildCallUrl(url, userName || client?.user?.name || 'Invitado');
      setActiveCall({ url: finalUrl, channelId: channelId || null, title: title || 'Videollamada' });
      setIncomingCall(null);
    };
    window.addEventListener('iba:open-video-call', handler);
    return () => window.removeEventListener('iba:open-video-call', handler);
  }, [client]);

  // Cierra la videollamada activa y publica "Llamada finalizada" en el canal
  const endActiveCall = async () => {
    const call = activeCall;
    setActiveCall(null);
    if (!call || !client) return;
    try {
      const cid = call.channelId;
      if (cid) {
        // cid formato: "messaging:xxx" o "team:xxx"
        const [type, id] = String(cid).split(':');
        if (type && id) {
          const ch = client.channel(type, id);
          await ch.watch();
          await ch.sendMessage({
            text: '📵 Llamada finalizada',
            video_call_ended: true,
            silent: true
          });
        }
      }
    } catch (e) {
      console.warn('No se pudo enviar mensaje de llamada finalizada:', e?.message);
    }
  };

  // Sonido de timbre cuando hay llamada entrante (Web Audio API, sin archivos externos)
  useEffect(() => {
    if (!incomingCall) return;

    let audioCtx = null;
    let interval = null;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        audioCtx = new Ctx();
        const playRing = () => {
          if (!audioCtx) return;
          const now = audioCtx.currentTime;
          // Dos tonos cortos "ring ring" estilo teléfono
          [0, 0.35].forEach(offset => {
            const osc = audioCtx.createOscillator();
            const gain = audioCtx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(880, now + offset);
            osc.frequency.setValueAtTime(660, now + offset + 0.15);
            gain.gain.setValueAtTime(0.0001, now + offset);
            gain.gain.exponentialRampToValueAtTime(0.4, now + offset + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.3);
            osc.connect(gain).connect(audioCtx.destination);
            osc.start(now + offset);
            osc.stop(now + offset + 0.32);
          });
        };
        playRing();
        interval = setInterval(playRing, 2500);
      }
    } catch (_) {}

    // Vibración en móvil
    try { if (navigator.vibrate) navigator.vibrate([500, 300, 500, 300, 500]); } catch (_) {}
    const vibrateInterval = setInterval(() => {
      try { if (navigator.vibrate) navigator.vibrate([500, 300, 500]); } catch (_) {}
    }, 3000);

    // Auto-cerrar después de 45 segundos si no aceptó/rechazó
    const timeout = setTimeout(() => setIncomingCall(null), 45000);

    return () => {
      if (interval) clearInterval(interval);
      clearInterval(vibrateInterval);
      clearTimeout(timeout);
      try { if (audioCtx) audioCtx.close(); } catch (_) {}
      try { if (navigator.vibrate) navigator.vibrate(0); } catch (_) {}
    };
  }, [incomingCall]);

  const getToken = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  useEffect(() => {
    // Cancellation flag para evitar setState después de unmount
    let cancelled = false;
    // Listener registrado en este montaje (lo desregistramos en cleanup)
    let messageListener = null;
    let chatClient = null;

    const initChat = async () => {
      try {
        // Get token from backend
        const res = await axios.get(`${API}/stream-chat/token`, { headers: headers() });
        if (cancelled) return;
        const { token, user_id, user_name, user_image, api_key } = res.data;
        setUserData(res.data);

        // Sync user to Stream (non-blocking - chat works even if this fails)
        try {
          await axios.post(`${API}/stream-chat/sync-user`, {}, { headers: headers() });
        } catch (syncErr) {
          console.warn('Stream sync-user failed (non-critical):', syncErr.message);
        }
        if (cancelled) return;

        // Initialize Stream Chat client (singleton compartido)
        chatClient = StreamChat.getInstance(api_key || STREAM_KEY);

        // ────────────────────────────────────────────────────────────────
        // FIX: "connectUser was called twice"
        // Stream Chat usa un singleton. Si el componente se monta 2 veces
        // rápido (StrictMode, navegación rápida, retry), o si hay otra
        // pestaña con sesión previa, el singleton ya tiene un usuario.
        // 1) Si ya está conectado con el mismo user_id → reutilizar
        // 2) Si hay una conexión en curso → esperarla
        // 3) Si está conectado con OTRO user_id → desconectar primero
        // ────────────────────────────────────────────────────────────────
        if (chatClient.userID === user_id) {
          // Ya conectado con este mismo usuario, no reconectar
          if (!cancelled) setClient(chatClient);
        } else {
          // Si hay una promesa de conexión en curso (otro mount), esperarla
          const inFlight = window.__iba_streamConnectPromise;
          if (inFlight) {
            try { await inFlight; } catch (_) { /* ignorar errores previos */ }
          }
          if (cancelled) return;

          // Después de esperar, re-chequear si quedó conectado con este user
          if (chatClient.userID === user_id) {
            if (!cancelled) setClient(chatClient);
          } else {
            // Si está conectado con otro userID (sesión vieja), desconectar
            if (chatClient.userID && chatClient.userID !== user_id) {
              try { await chatClient.disconnectUser(); } catch (_) {}
              if (cancelled) return;
            }

            // Connect (registramos la promesa para dedupar mounts paralelos)
            const connectPromise = chatClient.connectUser(
              { id: user_id, name: user_name, image: user_image || undefined },
              token,
            );
            window.__iba_streamConnectPromise = connectPromise;
            try {
              await connectPromise;
            } finally {
              if (window.__iba_streamConnectPromise === connectPromise) {
                window.__iba_streamConnectPromise = null;
              }
            }
            if (cancelled) return;
            setClient(chatClient);
          }
        }

        // Load out-of-office status
        try {
          const oooRes = await axios.get(`${API}/stream-chat/out-of-office`, { headers: headers() });
          if (!cancelled) {
            setOooEnabled(oooRes.data.enabled || false);
            setOooMessage(oooRes.data.message || '');
            setOooAutoReplyAI(oooRes.data.auto_reply_ai || false);
            setOooAutoActive(oooRes.data.auto_active || false);
            setOooOverrideActive(oooRes.data.override_active || false);
          }
        } catch (e) {}

        // Load schedule
        try {
          const schedRes = await axios.get(`${API}/stream-chat/schedule`, { headers: headers() });
          if (!cancelled) setSchedule(schedRes.data.days || {});
        } catch (e) {}

        // Listen for outgoing messages to trigger push notifications + AI
        const AI_BOT_ID = 'genesis_ai_bot';

        messageListener = async (event) => {
          try {
            // Deduplicate - prevent double processing (React StrictMode)
            const msgId = event.message?.id;
            if (!msgId || _processedAIMessageIds.has(msgId)) return;
            _processedAIMessageIds.add(msgId);
            if (_processedAIMessageIds.size > 200) {
              const arr = [..._processedAIMessageIds];
              arr.slice(0, 100).forEach(id => _processedAIMessageIds.delete(id));
            }
            // Only process if WE are the sender
            if (event.user?.id !== user_id) return;
            // Don't process bot's own messages
            if (event.user?.id === AI_BOT_ID) return;

            // Get the channel instance
            const ch = chatClient.channel(event.channel_type, event.channel_id);

            // If this is a DM with the AI bot, trigger AI response
            if (event.channel_type === 'messaging') {
              const members = Object.keys(ch.state?.members || {});
              const isAIChat = members.includes(AI_BOT_ID);
              if (isAIChat) {
                const msgText = event.message?.text || '';
                const rawAttachments = event.message?.attachments || [];
                const attachments = rawAttachments.map(a => ({
                  type: a.type || a.mime_type || '',
                  asset_url: a.asset_url || a.image_url || a.thumb_url || a.og_scrape_url || '',
                  title: a.title || a.fallback || a.name || '',
                  mime_type: a.mime_type || a.type || '',
                })).filter(a => a.asset_url);
                if (msgText || attachments.length > 0) {
                  axios.post(`${API}/stream-chat/ai-respond`, {
                    message_text: msgText,
                    channel_id: event.channel_id,
                    channel_type: 'messaging',
                    sender_name: user_name,
                    attachments: attachments
                  }, { headers: headers() }).catch((err) => {
                    console.warn('[Genesis AI] Error:', err?.response?.data || err.message);
                  });
                }
                return; // Don't send push for AI chats
              }
            }

            // Query ALL members from Stream (not local state which may be incomplete)
            let memberIds = [];
            try {
              const memberResponse = await ch.queryMembers({});
              memberIds = (memberResponse.members || [])
                .map(m => m.user_id || m.user?.id)
                .filter(id => id && id !== user_id && id !== AI_BOT_ID);
            } catch (qErr) {
              // Fallback to local state if query fails
              memberIds = Object.keys(ch.state?.members || {}).filter(id => id !== user_id && id !== AI_BOT_ID);
            }

            if (memberIds.length === 0) return;

            // Build message text
            const msgText = event.message?.text ||
              (event.message?.attachments?.length > 0 ? 'Envio un archivo' : '');

            axios.post(`${API}/stream-chat/notify`, {
              recipient_ids: memberIds,
              sender_name: user_name,
              message_text: msgText,
              channel_name: ch.data?.name || '',
              channel_type: event.channel_type || 'messaging',
              channel_id: event.channel_id || ''
            }, { headers: headers() }).catch(() => {}); // Fire and forget

            // Check for auto-reply (out of office) in DMs
            if (event.channel_type === 'messaging') {
              axios.post(`${API}/stream-chat/check-auto-reply`, {
                recipient_ids: memberIds,
                channel_id: event.channel_id,
                channel_type: 'messaging'
              }, { headers: headers() }).then(res => {
                const replies = res.data?.replies || [];
                replies.forEach(r => {
                  toast.info(`${r.name} esta fuera de oficina: "${r.message}"`, { duration: 6000 });
                });
              }).catch(() => {});

              // 🤖 Disparar Genesis AI auto-respond si el destinatario tiene OOO + AI activado.
              // El backend valida los permisos y dedupe por (ooo_session_id, sender).
              const recipientId = memberIds[0]; // En DM 1-a-1, el primer member que no soy yo
              const msgText2 = event.message?.text || '';
              if (recipientId && msgText2) {
                axios.post(`${API}/stream-chat/ooo-ai-autorespond`, {
                  recipient_id: recipientId,
                  channel_id: event.channel_id,
                  channel_type: 'messaging',
                  message_text: msgText2,
                }, { headers: headers() }).catch(() => {}); // Silent: si falla, no rompemos el chat
              }
            }
          } catch (e) {
            // Silent - don't break chat if push fails
            console.warn('Push notify error:', e);
          }
        };
        chatClient.on('message.new', messageListener);
      } catch (err) {
        console.error('Stream Chat init error:', err);
        if (!cancelled) setError(err.response?.data?.detail || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    initChat();

    return () => {
      cancelled = true;
      // IMPORTANTE: NO desconectar el singleton aquí. Si lo hacemos, el
      // próximo mount (StrictMode/navigation) intentaría reconectar y
      // chocaría con el "connectUser was called twice" o dejaría al usuario
      // sin chat. El singleton vive a nivel app y se desconecta solo en logout.
      // Solo removemos NUESTRO listener de eventos.
      try {
        if (chatClient && messageListener) {
          chatClient.off('message.new', messageListener);
        }
      } catch (_) {}
    };
  }, []);

  // Hide ElevenLabs widget on chat page
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'hide-elevenlabs-chat';
    style.textContent = 'elevenlabs-convai, .elevenlabs-widget { display: none !important; visibility: hidden !important; }';
    document.head.appendChild(style);
    return () => { style.remove(); };
  }, []);

  const toggleOutOfOffice = async (enabled, message, autoReplyAI = oooAutoReplyAI) => {
    try {
      const res = await axios.post(`${API}/stream-chat/out-of-office`, { enabled, message, auto_reply_ai: !!autoReplyAI }, { headers: headers() });
      setOooEnabled(enabled);
      setOooMessage(message);
      setOooAutoReplyAI(!!autoReplyAI);
      const pendingSenders = res.data?.pending_senders || [];
      // Si se desactiva y hay senders pendientes → abrir modal de retorno
      if (!enabled && pendingSenders.length > 0) {
        setOooReturnModal({ pending_senders: pendingSenders, notifyChecked: false });
        setShowOooPanel(false);
        toast.success('Fuera de oficina desactivado');
      } else {
        toast.success(enabled ? 'Fuera de oficina activado' : 'Fuera de oficina desactivado');
        setShowOooPanel(false);
      }
    } catch (e) {
      toast.error('Error al actualizar estado');
    }
  };

  const sendNotifyBack = async (custom) => {
    try {
      const res = await axios.post(`${API}/stream-chat/ooo-notify-back`,
        custom ? { custom_message: custom } : {},
        { headers: headers() }
      );
      const sent = res.data?.sent || 0;
      toast.success(`Aviso enviado a ${sent} ${sent === 1 ? 'conversación' : 'conversaciones'}`);
    } catch (e) {
      toast.error('Error al notificar');
    } finally {
      setOooReturnModal(null);
    }
  };

  const saveSchedule = async (days) => {
    setSavingSchedule(true);
    try {
      await axios.post(`${API}/stream-chat/schedule`, { days }, { headers: headers() });
      setSchedule(days);
      toast.success('Horario guardado');
      setShowSchedulePanel(false);
    } catch (e) {
      toast.error('Error al guardar horario');
    } finally {
      setSavingSchedule(false);
    }
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-120px)]" data-testid="stream-chat-loading">
        <div className="text-center space-y-4">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto" />
          <p className="text-gray-500">Conectando al chat...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-120px)]" data-testid="stream-chat-error">
        <div className="text-center space-y-4 max-w-md">
          <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
            <MessageSquare className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">Error de conexion</h3>
          <p className="text-gray-500 text-sm">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!client) return null;

  // 🔧 FIX 2026-05-14 v2: Stream Chat no soporta filtrar por custom fields
  // como `scope` a nivel query — los excluía silenciosamente. Volvemos al
  // filtro nativo y excluimos los canales de Sala Docente CLIENT-SIDE vía
  // `channelRenderFilterFn` (más abajo). Así no se pierden canales por error.
  const directFilters = {
    type: 'messaging',
    members: { $in: [userData.user_id] },
  };
  const channelFilters = {
    type: 'team',
    members: { $in: [userData.user_id] },
  };
  // Filtro client-side: oculta canales con scope='area_profesores' (Sala Docente).
  const hideAreaProfesores = (channels) =>
    (channels || []).filter((ch) => ch?.data?.scope !== 'area_profesores');
  const sort = { last_message_at: -1 };
  const channelSort = { name: 1 };
  const options = { limit: 30, presence: true, state: true };
  const isAdmin = userData?.user_role === 'admin';

  return (
    <div className="h-[calc(100vh-120px)] rounded-xl overflow-hidden shadow-lg border" data-testid="stream-chat-container">
      {/* Panel Horarios */}
      {showSchedulePanel && (
        <SchedulePanel
          schedule={schedule}
          onSave={saveSchedule}
          onClose={() => setShowSchedulePanel(false)}
          saving={savingSchedule}
        />
      )}
      {/* Panel Fuera de Oficina */}
      {showOooPanel && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="ooo-panel">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${oooEnabled ? 'bg-amber-100' : 'bg-gray-100'}`}>
                {oooEnabled ? <BellOff className="w-5 h-5 text-amber-600" /> : <Bell className="w-5 h-5 text-gray-400" />}
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Fuera de oficina</h4>
                <p className="text-xs text-gray-500">{oooEnabled ? 'Activo - no recibiras notificaciones' : 'Desactivado'}</p>
              </div>
            </div>
            {/* Aviso de auto-OOO por horario (solo si manual está OFF y el sistema te marca fuera) */}
            {!oooEnabled && oooAutoActive && (
              <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-3 text-xs text-amber-800" data-testid="ooo-auto-banner">
                <div className="font-semibold flex items-center gap-1.5 mb-0.5">
                  <i className="fas fa-clock" /> Fuera de horario de atención
                </div>
                <p className="leading-snug mb-2">
                  El sistema te marca automáticamente como Fuera de Oficina porque estás fuera del horario que configuraste en tu perfil. Se desactivará solo cuando vuelvas al horario.
                </p>
                <button
                  onClick={async () => {
                    try {
                      // Override hasta mañana 00:00 en Montevideo (siguiente día calendario)
                      const tzNow = new Date();
                      const mañana = new Date(tzNow);
                      mañana.setHours(24, 0, 0, 0); // próximo medianoche local del browser
                      await axios.post(`${API}/stream-chat/out-of-office/auto-override`,
                        { until: mañana.toISOString() },
                        { headers: headers() }
                      );
                      setOooAutoActive(false);
                      setOooOverrideActive(true);
                      toast.success('Auto-OOO anulado hasta mañana. Aparecés como disponible.');
                    } catch (e) {
                      toast.error('No se pudo anular el auto-OOO');
                    }
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs px-3 py-1.5 rounded-lg shadow-sm"
                  data-testid="ooo-override-btn"
                >
                  <i className="fas fa-circle-check mr-1" />
                  Estoy disponible (anular hasta mañana)
                </button>
              </div>
            )}
            {/* Aviso cuando el override está activo (el auto NO se aplica aunque esté fuera de horario) */}
            {!oooEnabled && oooOverrideActive && (
              <div className="bg-emerald-50 border-l-4 border-emerald-400 rounded-r-lg p-3 text-xs text-emerald-800" data-testid="ooo-override-banner">
                <div className="font-semibold flex items-center gap-1.5 mb-0.5">
                  <i className="fas fa-circle-check" /> Disponibilidad forzada activa
                </div>
                <p className="leading-snug mb-2">
                  Anulaste el auto-OOO. Aparecés como disponible aunque estés fuera de tu horario de atención. Se restablece automáticamente mañana.
                </p>
                <button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/stream-chat/out-of-office/auto-override`,
                        { until: null },
                        { headers: headers() }
                      );
                      setOooOverrideActive(false);
                      // Re-fetch para saber si ahora aplica el auto
                      try {
                        const r = await axios.get(`${API}/stream-chat/out-of-office`, { headers: headers() });
                        setOooAutoActive(r.data.auto_active || false);
                      } catch {}
                      toast.success('Override desactivado. El auto-OOO vuelve a regir tu horario.');
                    } catch (e) {
                      toast.error('No se pudo restablecer');
                    }
                  }}
                  className="bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs px-3 py-1.5 rounded-lg shadow-sm"
                  data-testid="ooo-override-cancel-btn"
                >
                  <i className="fas fa-arrow-rotate-left mr-1" />
                  Restaurar (volver al horario)
                </button>
              </div>
            )}
            {!oooEnabled ? (
              <>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Mensaje de respuesta automatica</label>
                  <textarea
                    value={oooMessage}
                    onChange={e => setOooMessage(e.target.value)}
                    placeholder="Ej: Estoy fuera hasta el lunes. Dios les bendiga."
                    className="w-full border rounded-lg px-3 py-2 text-sm resize-none h-20"
                    data-testid="ooo-message-input"
                  />
                </div>
                {(userData?.user_role === 'admin' || userData?.user_role === 'profesor') && (
                  <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors" data-testid="ooo-ai-toggle">
                    <input
                      type="checkbox"
                      checked={oooAutoReplyAI}
                      onChange={e => setOooAutoReplyAI(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-blue-600 cursor-pointer"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-blue-900">🤖 Responder con Genesis AI</div>
                      <div className="text-xs text-blue-700 leading-snug">El bot responderá automáticamente las consultas de los DMs mientras estés fuera. Tú verás todo cuando regreses.</div>
                    </div>
                  </label>
                )}
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setShowOooPanel(false)} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg">Cancelar</button>
                  <button
                    onClick={() => toggleOutOfOffice(true, oooMessage, oooAutoReplyAI)}
                    disabled={!oooMessage.trim()}
                    className="px-4 py-2 text-sm text-white bg-amber-500 hover:bg-amber-600 rounded-lg disabled:opacity-50"
                    data-testid="ooo-activate-btn"
                  >
                    Activar
                  </button>
                </div>
              </>
            ) : (
              <>
                {oooAutoReplyAI && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                    🤖 Genesis AI está respondiendo automáticamente tus DMs.
                  </div>
                )}
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setShowOooPanel(false)} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg">Cerrar</button>
                  <button
                    onClick={() => toggleOutOfOffice(false, oooMessage, oooAutoReplyAI)}
                    className="px-4 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg"
                    data-testid="ooo-deactivate-btn"
                  >
                    Desactivar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Modal de retorno OOO — aparece al desactivar si hubo mensajes */}
      {oooReturnModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="ooo-return-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                <Bell className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">¡Bienvenido de vuelta!</h4>
                <p className="text-xs text-gray-500">Mientras estuviste fuera te escribieron {oooReturnModal.pending_senders.length} {oooReturnModal.pending_senders.length === 1 ? 'persona' : 'personas'}</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-auto space-y-1">
              {oooReturnModal.pending_senders.map((s, i) => (
                <div key={i} className="text-sm text-gray-700">• {s.name || s.email}</div>
              ))}
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={oooReturnModal.notifyChecked}
                onChange={e => setOooReturnModal({ ...oooReturnModal, notifyChecked: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-green-600 cursor-pointer"
                data-testid="ooo-notify-checkbox"
              />
              <div className="text-sm text-gray-700">
                <span className="font-medium">Avisarles que ya estoy disponible</span>
                <div className="text-xs text-gray-500">Se enviará un mensaje en cada DM diciendo que ya regresaste.</div>
              </div>
            </label>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setOooReturnModal(null)}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Más tarde
              </button>
              <button
                onClick={() => oooReturnModal.notifyChecked ? sendNotifyBack() : setOooReturnModal(null)}
                className="px-4 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg"
                data-testid="ooo-return-confirm-btn"
              >
                {oooReturnModal.notifyChecked ? 'Enviar y cerrar' : 'Cerrar'}
              </button>
            </div>
          </div>
        </div>
      )}
      <Chat client={client} theme="messaging light">
        <OOOProvider>
        <ChannelDeepLink />
        {isMobile ? (
          /* Mobile: sliding panels */
          <div className="h-full overflow-hidden relative">
            <div 
              className="flex h-full transition-transform duration-300 ease-in-out"
              style={{ 
                width: '200%',
                transform: mobileShowChat ? 'translateX(-50%)' : 'translateX(0)'
              }}
            >
              {/* Mobile sidebar - 50% of 200% = full screen */}
              <div className="w-1/2 h-full flex flex-col bg-white">
                <div className="p-3 pl-14 border-b bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="font-bold text-gray-800 text-lg">Chat IBA Uruguay</h2>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setShowOooPanel(!showOooPanel)}
                        className={`p-2 rounded-lg transition-colors ${(oooEnabled || oooAutoActive) ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'}`}
                        title={oooEnabled ? 'Fuera de oficina (activo manualmente)' : oooAutoActive ? 'Fuera de horario de atención (automático)' : 'Disponible · Click para configurar tu Fuera de oficina'}
                        data-testid="ooo-toggle-btn"
                      >
                        {(oooEnabled || oooAutoActive) ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                      </button>
                      {(isAdmin || userData?.user_role === 'profesor') && (
                        <button
                          onClick={() => setShowSchedulePanel(true)}
                          className="p-2 rounded-lg transition-colors hover:bg-blue-100 text-blue-500"
                          title="Mi Horario"
                          data-testid="schedule-btn"
                        >
                          <Clock className="w-4 h-4" />
                        </button>
                      )}
                      {(activeTab === 'direct' || isAdmin) && (
                        <button
                          onClick={() => {
                            if (activeTab === 'direct') {
                              setShowNewChat(true); setShowNewChannel(false);
                            } else {
                              setShowNewChannel(true); setShowNewChat(false);
                            }
                          }}
                          className={`p-2 rounded-lg transition-colors ${activeTab === 'direct' ? 'hover:bg-blue-100' : 'hover:bg-green-100'}`}
                          title={activeTab === 'direct' ? 'Nuevo chat' : 'Nuevo canal'}
                          data-testid="new-item-btn"
                        >
                          <Plus className={`w-4 h-4 ${activeTab === 'direct' ? 'text-blue-600' : 'text-green-600'}`} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex bg-gray-200 rounded-lg p-0.5" data-testid="chat-tabs">
                    <button
                      onClick={() => { setActiveTab('direct'); setShowNewChannel(false); }}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        activeTab === 'direct' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      }`}
                      data-testid="tab-direct"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      Directos
                    </button>
                    <button
                      onClick={() => { setActiveTab('channels'); setShowNewChat(false); }}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        activeTab === 'channels' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      }`}
                      data-testid="tab-channels"
                    >
                      <Hash className="w-3.5 h-3.5" />
                      Canales
                    </button>
                  </div>
                </div>

                {showNewChat && activeTab === 'direct' && (
                  <NewChatPanel onClose={() => setShowNewChat(false)} client={client} currentUserId={userData.user_id} userRole={userData.user_role} />
                )}
                {showNewChannel && activeTab === 'channels' && (
                  <NewChannelPanel onClose={() => setShowNewChannel(false)} client={client} currentUserId={userData.user_id} />
                )}

                {activeTab === 'direct' ? (
                  <ChannelList
                    key="direct"
                    filters={directFilters}
                    sort={sort}
                    options={options}
                    showChannelSearch
                    channelRenderFilterFn={hideAreaProfesores}
                    Preview={(props) => <CustomChannelPreview {...props} onMobileSelect={() => setMobileShowChat(true)} />}
                    EmptyStateIndicator={() => (
                      <div className="p-6 text-center text-gray-400 text-sm">
                        <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No tienes chats directos</p>
                        <p className="text-xs mt-1">Toca + para iniciar uno</p>
                      </div>
                    )}
                  />
                ) : (
                  <ChannelList
                    key="channels"
                    filters={channelFilters}
                    sort={channelSort}
                    options={options}
                    showChannelSearch
                    channelRenderFilterFn={hideAreaProfesores}
                    Preview={(props) => <CustomChannelPreview {...props} onMobileSelect={() => setMobileShowChat(true)} />}
                    EmptyStateIndicator={() => (
                      <div className="p-6 text-center text-gray-400 text-sm">
                        <Hash className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No tienes canales</p>
                        <p className="text-xs mt-1">Toca + para crear uno</p>
                      </div>
                    )}
                  />
                )}
              </div>

              {/* Mobile chat area - 50% of 200% = full screen */}
              <div className="w-1/2 h-full flex flex-col">
                <Channel EmojiPicker={EmojiPicker} Message={CustomMessage}>
                  <Window>
                    <CustomChannelHeader onBackToList={() => setMobileShowChat(false)} isAdmin={isAdmin} userRole={userData?.user_role} />
                    <MessageList />
                    <CustomMessageInput canCreatePolls={isAdmin || userData?.user_role === 'profesor'} />
                  </Window>
                  <Thread />
                </Channel>
              </div>
            </div>
          </div>
        ) : (
          /* Desktop: side by side */
          <div className="flex h-full">
            <div className="w-80 border-r flex flex-col bg-white">
              <div className="p-3 border-b bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="font-bold text-gray-800 text-lg">Chat IBA</h2>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setShowOooPanel(!showOooPanel)}
                      className={`p-2 rounded-lg transition-colors ${(oooEnabled || oooAutoActive) ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'}`}
                      title={oooEnabled ? 'Fuera de oficina (activo manualmente)' : oooAutoActive ? 'Fuera de horario de atención (automático)' : 'Disponible · Click para configurar tu Fuera de oficina'}
                      data-testid="ooo-toggle-btn"
                    >
                      {(oooEnabled || oooAutoActive) ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                    </button>
                    {(isAdmin || userData?.user_role === 'profesor') && (
                      <button
                        onClick={() => setShowSchedulePanel(true)}
                        className="p-2 rounded-lg transition-colors hover:bg-blue-100 text-blue-500"
                        title="Mi Horario"
                        data-testid="schedule-btn-desktop"
                      >
                        <Clock className="w-4 h-4" />
                      </button>
                    )}
                    {(activeTab === 'direct' || isAdmin) && (
                      <button
                        onClick={() => {
                          if (activeTab === 'direct') {
                            setShowNewChat(true); setShowNewChannel(false);
                          } else {
                            setShowNewChannel(true); setShowNewChat(false);
                          }
                        }}
                        className={`p-2 rounded-lg transition-colors ${activeTab === 'direct' ? 'hover:bg-blue-100' : 'hover:bg-green-100'}`}
                        title={activeTab === 'direct' ? 'Nuevo chat' : 'Nuevo canal'}
                        data-testid="new-item-btn"
                      >
                        <Plus className={`w-4 h-4 ${activeTab === 'direct' ? 'text-blue-600' : 'text-green-600'}`} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex bg-gray-200 rounded-lg p-0.5" data-testid="chat-tabs">
                  <button
                    onClick={() => { setActiveTab('direct'); setShowNewChannel(false); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      activeTab === 'direct' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid="tab-direct"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    Directos
                  </button>
                  <button
                    onClick={() => { setActiveTab('channels'); setShowNewChat(false); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      activeTab === 'channels' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid="tab-channels"
                  >
                    <Hash className="w-3.5 h-3.5" />
                    Canales
                  </button>
                </div>
              </div>

              {showNewChat && activeTab === 'direct' && (
                <NewChatPanel onClose={() => setShowNewChat(false)} client={client} currentUserId={userData.user_id} userRole={userData.user_role} />
              )}
              {showNewChannel && activeTab === 'channels' && (
                <NewChannelPanel onClose={() => setShowNewChannel(false)} client={client} currentUserId={userData.user_id} />
              )}

              {activeTab === 'direct' ? (
                <ChannelList
                  key="direct"
                  filters={directFilters}
                  sort={sort}
                  options={options}
                  showChannelSearch
                  channelRenderFilterFn={hideAreaProfesores}
                  Preview={(props) => <CustomChannelPreview {...props} />}
                  EmptyStateIndicator={() => (
                    <div className="p-6 text-center text-gray-400 text-sm">
                      <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No tienes chats directos</p>
                      <p className="text-xs mt-1">Toca + para iniciar uno</p>
                    </div>
                  )}
                />
              ) : (
                <ChannelList
                  key="channels"
                  filters={channelFilters}
                  sort={channelSort}
                  options={options}
                  showChannelSearch
                  channelRenderFilterFn={hideAreaProfesores}
                  Preview={(props) => <CustomChannelPreview {...props} />}
                  EmptyStateIndicator={() => (
                    <div className="p-6 text-center text-gray-400 text-sm">
                      <Hash className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No tienes canales</p>
                      <p className="text-xs mt-1">Toca + para crear uno</p>
                    </div>
                  )}
                />
              )}
            </div>

            <div className="flex-1 flex flex-col">
              <Channel EmojiPicker={EmojiPicker} Message={CustomMessage}>
                <Window>
                  <CustomChannelHeader isAdmin={isAdmin} userRole={userData?.user_role} />
                  <MessageList />
                  <CustomMessageInput />
                </Window>
                <Thread />
              </Channel>
            </div>
          </div>
        )}
        </OOOProvider>
      </Chat>

      {/* Modal de llamada entrante */}
      {incomingCall && createPortal(
        <div className="fixed inset-0 z-[10000] bg-black/80 flex items-center justify-center p-4 animate-in fade-in" data-testid="incoming-call-modal">
          <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden">
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-8 text-center text-white">
              <div className="w-24 h-24 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center animate-pulse">
                <Video className="w-12 h-12" />
              </div>
              <p className="text-xs uppercase tracking-widest opacity-80">Videollamada entrante</p>
              <h3 className="text-2xl font-bold mt-2">{incomingCall.caller}</h3>
              <p className="text-sm opacity-90 mt-1">te está llamando...</p>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <button
                onClick={() => setIncomingCall(null)}
                className="py-4 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold shadow-md transition-all active:scale-95"
                data-testid="reject-call-btn"
              >
                <i className="fas fa-phone-slash mr-2" />Rechazar
              </button>
              <button
                onClick={async () => {
                  const userName = client?.user?.name || 'Invitado';
                  const finalUrl = await buildCallUrl(incomingCall.url, userName);
                  setActiveCall({
                    url: finalUrl,
                    channelId: incomingCall.channelId,
                    title: incomingCall.caller || 'Videollamada'
                  });
                  setIncomingCall(null);
                }}
                className="py-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold shadow-md transition-all active:scale-95"
                data-testid="accept-call-btn"
              >
                <i className="fas fa-video mr-2" />Aceptar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Iframe global de videollamada activa (para receptores y móvil) */}
      {activeCall && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black flex flex-col" data-testid="active-call-panel">
          <div className="flex items-center justify-between px-3 py-2 bg-gray-900 shrink-0">
            <div className="flex items-center gap-2 text-white min-w-0">
              <Video className="w-4 h-4 shrink-0" />
              <span className="text-sm font-medium truncate">{activeCall.title}</span>
            </div>
            <button
              onClick={endActiveCall}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shrink-0 ml-2"
              data-testid="end-active-call-btn"
            >
              <X className="w-4 h-4" />
              <span className="hidden sm:inline">Volver al chat</span>
              <span className="sm:hidden">Salir</span>
            </button>
          </div>
          <iframe
            src={activeCall.url}
            className="flex-1 w-full border-0"
            allow="camera *; microphone *; fullscreen; display-capture; autoplay; clipboard-write; picture-in-picture"
            allowFullScreen
            data-testid="active-call-iframe"
          />
        </div>,
        document.body
      )}
    </div>
  );
};

// Custom Channel Preview - different colors for channels vs DMs
const CustomChannelPreview = (props) => {
  const { channel, setActiveChannel, activeChannel, displayTitle, latestMessage, unread, onMobileSelect } = props;
  const { client } = useChatContext();
  const isTeam = channel?.type === 'team';
  const isActive = activeChannel?.cid === channel?.cid;
  const channelName = channel?.data?.name || displayTitle || channel?.id || '';
  const lastMsg = typeof latestMessage === 'string' ? latestMessage : latestMessage?.props?.children || 'Sin mensajes';

  // Get avatar image + DM partner online status
  let avatarImage = null;
  let otherUserId = null;
  let otherIsOnline = false;
  if (isTeam) {
    avatarImage = channel?.data?.image || null;
  } else {
    const members = Object.values(channel?.state?.members || {});
    const otherMember = members.find(m => m.user_id !== client?.userID);
    avatarImage = otherMember?.user?.image || null;
    otherUserId = otherMember?.user_id || null;
    otherIsOnline = !!otherMember?.user?.online;
  }

  // Estado de OOO consumido del contexto global (dot rojo, prioridad sobre online).
  const oooSet = useContext(OOOContext);
  const isOOO = otherUserId && oooSet.has(otherUserId);
  // Re-render cuando cambia la presencia del otro user
  const [, _force] = useState(0);
  useEffect(() => {
    if (!client || !otherUserId) return;
    const onPresence = (ev) => {
      if (ev?.user?.id === otherUserId) _force((x) => x + 1);
    };
    client.on('user.presence.changed', onPresence);
    return () => { try { client.off('user.presence.changed', onPresence); } catch (_e) {} };
  }, [client, otherUserId]);

  let dotColor = null;
  let dotTitle = '';
  if (isOOO) { dotColor = 'bg-red-500'; dotTitle = 'Fuera de oficina'; }
  else if (otherIsOnline) { dotColor = 'bg-green-500'; dotTitle = 'En línea'; }

  return (
    <button
      onClick={() => {
        setActiveChannel(channel);
        if (onMobileSelect) onMobileSelect();
      }}
      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors border-l-3 ${
        isActive
          ? (isTeam ? 'bg-emerald-50 border-l-emerald-500' : 'bg-blue-50 border-l-blue-500')
          : 'hover:bg-gray-50 border-l-transparent'
      }`}
      style={{ borderLeftWidth: '3px' }}
      data-testid={`channel-preview-${channel?.id}`}
    >
      <div className="relative shrink-0">
        {avatarImage ? (
          <img src={avatarImage} alt="" className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm ${
            isTeam ? 'bg-emerald-500' : 'bg-blue-500'
          }`}>
            {isTeam ? <Hash className="w-4 h-4" /> : channelName.charAt(0).toUpperCase()}
          </div>
        )}
        {!isTeam && dotColor && (
          <span
            title={dotTitle}
            data-testid={`presence-dot-${otherUserId}-${isOOO ? 'ooo' : 'online'}`}
            className={`absolute bottom-0 right-0 w-2.5 h-2.5 ${dotColor} border-2 border-white rounded-full`}
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between">
          <p className={`text-sm font-medium truncate ${unread ? 'text-gray-900 font-bold' : 'text-gray-700'}`}>
            {isTeam && <span className="text-emerald-600"># </span>}
            {channelName}
          </p>
          {unread > 0 && (
            <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full text-white shrink-0 ${isTeam ? 'bg-emerald-500' : 'bg-blue-500'}`}>
              {unread}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-400 truncate">{lastMsg}</p>
      </div>
    </button>
  );
};


// Schedule Editor Panel
const DAYS = [
  { key: 'lun', label: 'Lunes' },
  { key: 'mar', label: 'Martes' },
  { key: 'mie', label: 'Miercoles' },
  { key: 'jue', label: 'Jueves' },
  { key: 'vie', label: 'Viernes' },
  { key: 'sab', label: 'Sabado' },
  { key: 'dom', label: 'Domingo' },
];

const SchedulePanel = ({ schedule, onSave, onClose, saving }) => {
  const [days, setDays] = useState(() => {
    const d = {};
    DAYS.forEach(({ key }) => {
      d[key] = schedule[key] || { enabled: false, from: '08:00', to: '17:00' };
    });
    return d;
  });

  const toggleDay = (key) => {
    setDays(prev => ({ ...prev, [key]: { ...prev[key], enabled: !prev[key].enabled } }));
  };

  const updateTime = (key, field, value) => {
    setDays(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="schedule-panel">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-4 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
            <Clock className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-800">Mi Horario de Atencion</h4>
            <p className="text-xs text-gray-500">Selecciona los dias y horas que atiendes</p>
          </div>
        </div>

        <div className="space-y-2">
          {DAYS.map(({ key, label }) => (
            <div key={key} className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${days[key].enabled ? 'bg-blue-50' : 'bg-gray-50'}`}>
              <label className="flex items-center gap-2 cursor-pointer min-w-[100px]">
                <input
                  type="checkbox"
                  checked={days[key].enabled}
                  onChange={() => toggleDay(key)}
                  className="rounded"
                />
                <span className={`text-sm font-medium ${days[key].enabled ? 'text-blue-800' : 'text-gray-400'}`}>{label}</span>
              </label>
              {days[key].enabled && (
                <div className="flex items-center gap-1 text-sm">
                  <input
                    type="time"
                    value={days[key].from}
                    onChange={e => updateTime(key, 'from', e.target.value)}
                    className="border rounded px-2 py-1 text-xs w-[90px]"
                  />
                  <span className="text-gray-400">a</span>
                  <input
                    type="time"
                    value={days[key].to}
                    onChange={e => updateTime(key, 'to', e.target.value)}
                    className="border rounded px-2 py-1 text-xs w-[90px]"
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg">Cancelar</button>
          <button
            onClick={() => onSave(days)}
            disabled={saving}
            className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
            data-testid="save-schedule-btn"
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};


// Custom Channel Header with member management for team channels
const CustomChannelHeader = ({ onBackToList, isAdmin, userRole }) => {
  const { channel } = useChannelStateContext();
  const { client, setActiveChannel } = useChatContext();
  const [showMembers, setShowMembers] = useState(false);
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTruncateConfirm, setShowTruncateConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editName, setEditName] = useState('');
  const [editImage, setEditImage] = useState('');
  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  const isTeamChannel = channel?.type === 'team';
  const memberCount = Object.keys(channel?.state?.members || {}).length;
  const isCreator = channel?.data?.created_by_id === client?.userID || channel?.data?.created_by?.id === client?.userID;
  
  // For DMs, show the other member's name instead of channel ID
  let channelName = channel?.data?.name || '';
  if (!isTeamChannel && !channelName) {
    const members = Object.values(channel?.state?.members || {});
    const otherMember = members.find(m => m.user_id !== client?.userID);
    channelName = otherMember?.user?.name || otherMember?.user_id || channel?.id || '';
  }
  if (!channelName) channelName = channel?.id || '';

  const [showVideo, setShowVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');

  const startVideoCall = async () => {
    if (!channel?.id) {
      toast.error('No se pudo iniciar la videollamada');
      return;
    }
    const toastId = toast.loading('Creando sala de videollamada...');
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const origin = (typeof window !== 'undefined' && window.location?.origin) || '';

      // Recolectar los IDs de los demás miembros del canal para que el backend
      // les mande push (OneSignal). Stream Chat NO envía push por sí solo en
      // esta integración — el backend de IBA es el que dispara OneSignal.
      const membersMap = channel?.state?.members || {};
      const notifyUserIds = Object.values(membersMap)
        .map((m) => m?.user_id || m?.user?.id)
        .filter((uid) => uid && uid !== client?.userID);

      const resp = await fetch(`${origin}/api/chat/video-room`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          channel_id: channel.id,
          channel_name: channel?.data?.name || '',
          is_team: !!isTeamChannel,
          notify_user_ids: notifyUserIds
        })
      });
      if (!resp.ok) throw new Error('No se pudo crear la sala');
      const data = await resp.json();
      toast.dismiss(toastId);

      const callerName = client?.user?.name || 'Alguien';

      // Enviar también el mensaje al chat con metadata para el popup in-app.
      try {
        await channel.sendMessage({
          text: `📹 ${callerName} inició una videollamada. Tocá para unirte: ${data.url}`,
          video_call: true,
          video_call_url: data.url,
          video_call_caller: callerName,
          video_call_started_at: Date.now()
        });
      } catch (e) {
        console.error('No se pudo enviar mensaje al chat:', e);
      }

      const finalUrl = await buildCallUrl(data.url, callerName);
      setVideoUrl(finalUrl);
      setShowVideo(true);
    } catch (e) {
      toast.dismiss(toastId);
      console.error('Error iniciando videollamada:', e);
      toast.error('No se pudo iniciar la videollamada. Intentá de nuevo.');
    }
  };

  const deleteChannel = async () => {
    setDeleting(true);
    try {
      await channel.delete();
      toast.success(`${isTeamChannel ? 'Canal' : 'Chat'} "${channelName}" eliminado`);
      setShowDeleteConfirm(false);
      setActiveChannel(null);
      if (onBackToList) onBackToList();
    } catch (err) {
      toast.error('Error eliminando canal: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const truncateChannel = async () => {
    setDeleting(true);
    try {
      await channel.truncate();
      toast.success(`Todos los mensajes de "${channelName}" fueron eliminados`);
      setShowTruncateConfirm(false);
    } catch (err) {
      toast.error('Error borrando mensajes: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const openEdit = () => {
    setEditName(channel?.data?.name || '');
    setEditImage(channel?.data?.image || '');
    setShowEdit(true);
  };

  const saveEdit = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      const updateData = { name: editName.trim() };
      if (editImage.trim()) {
        updateData.image = editImage.trim();
      } else {
        updateData.image = '';
      }
      await channel.updatePartial({ set: updateData });
      toast.success('Canal actualizado');
      setShowEdit(false);
    } catch (err) {
      toast.error('Error actualizando canal: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const openMemberPanel = async () => {
    setShowMembers(true);
    setLoadingMembers(true);
    try {
      // Get current members
      const currentMembers = Object.values(channel.state.members || {}).map(m => ({
        id: m.user_id || m.user?.id,
        name: m.user?.name || m.user_id,
        role: m.role,
        image: m.user?.image || '',
      }));
      setMembers(currentMembers);

      // Load all users
      const res = await axios.get(`${API}/stream-chat/users`, { headers: headers() });
      setAllUsers(res.data.users || []);
    } catch (err) {
      toast.error('Error cargando miembros');
    } finally {
      setLoadingMembers(false);
    }
  };

  const addMember = async (userId) => {
    try {
      // Ensure user exists in Stream Chat before adding to channel
      const user = allUsers.find(u => u.id === userId);
      if (user?.email) {
        await axios.post(`${API}/stream-chat/ensure-user/${encodeURIComponent(user.email)}`, {}, { headers: headers() });
      }
      await channel.addMembers([userId]);
      setMembers(prev => [...prev, { id: userId, name: user?.name || userId }]);
      toast.success('Miembro agregado');
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  const removeMember = async (userId) => {
    if (userId === client.userID) {
      toast.error('No puedes removerte a ti mismo');
      return;
    }
    try {
      await channel.removeMembers([userId]);
      setMembers(prev => prev.filter(m => m.id !== userId));
      toast.success('Miembro removido');
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  // Filter nonMembers without the search condition - show all, search just filters
  const nonMembers = allUsers.filter(u =>
    !members.find(m => m.id === u.id)
  );

  return (
    <div className="relative">
      <div className={`flex items-center justify-between py-2 border-b bg-white ${onBackToList ? 'pl-14 pr-4' : 'px-4'}`} data-testid="channel-header">
        <div className="flex items-center gap-2">
          {/* Back button - visible only on mobile */}
          {onBackToList && (
            <button
              onClick={onBackToList}
              className="p-1.5 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="back-to-list-btn"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
          )}
          <div>
            <h3 className="font-semibold text-gray-800">{isTeamChannel ? '#' : ''} {channelName}</h3>
            <p className="text-xs text-gray-400">{memberCount} miembro{memberCount !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Botón de videollamada:
              - En canales de clase (team): SOLO admin/profesor pueden iniciar.
                Los estudiantes igual pueden UNIRSE si reciben el link en el chat.
              - En DMs (messaging): cualquiera puede iniciar como siempre. */}
          {(!isTeamChannel || isAdmin || userRole === 'profesor') && (
            <button
              onClick={startVideoCall}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors"
              title="Videollamada"
              data-testid="video-call-btn"
            >
              <Video className="w-3.5 h-3.5" />
            </button>
          )}
          {isTeamChannel && isAdmin && (
            <>
              <button
                onClick={openEdit}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                title="Editar canal"
                data-testid="edit-channel-btn"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={openMemberPanel}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                data-testid="manage-members-btn"
              >
                <Users className="w-3.5 h-3.5" />
                Miembros
              </button>
              <button
                onClick={() => setShowTruncateConfirm(true)}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-amber-50 hover:bg-amber-100 text-amber-600 rounded-lg transition-colors"
                title="Borrar todos los mensajes"
                data-testid="truncate-channel-btn"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Limpiar</span>
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                title="Eliminar canal"
                data-testid="delete-channel-btn"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
          {!isTeamChannel && isAdmin && (
            <button
              onClick={() => setShowTruncateConfirm(true)}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-amber-50 hover:bg-amber-100 text-amber-600 rounded-lg transition-colors"
              title="Borrar todos los mensajes"
              data-testid="truncate-dm-btn"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Limpiar</span>
            </button>
          )}
          {!isTeamChannel && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
              title="Eliminar chat"
              data-testid="delete-channel-btn"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      {showMembers && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="members-panel">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-3 border-b bg-gray-50 rounded-t-xl">
              <span className="text-sm font-semibold text-gray-700">Miembros del canal</span>
              <button onClick={() => setShowMembers(false)} className="p-1 hover:bg-gray-200 rounded"><X className="w-4 h-4" /></button>
            </div>
            {loadingMembers ? (
              <div className="flex-1 flex items-center justify-center p-8">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {/* Miembros actuales */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Miembros actuales ({members.length})</p>
                  <div className="space-y-0.5">
                    {members.map(m => (
                      <div key={m.id} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded text-sm">
                        {m.image ? (
                          <img src={m.image} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-semibold shrink-0">
                            {(m.name || m.id).charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className="text-gray-800 font-medium flex-1 truncate">{m.name || m.id}</span>
                        {m.id !== client.userID && (
                          <button onClick={() => removeMember(m.id)} className="text-xs text-red-400 hover:text-red-600 shrink-0">Quitar</button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {/* Agregar miembros */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Agregar miembros</p>
                  <div className="relative mb-2">
                    <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
                    <input
                      type="text"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Filtrar usuarios..."
                      className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm"
                      data-testid="add-member-search"
                    />
                  </div>
                  <div className="space-y-0.5">
                    {nonMembers.length === 0 ? (
                      <p className="p-2 text-center text-gray-400 text-xs">Todos ya son miembros</p>
                    ) : (
                      nonMembers
                        .filter(u => !search || u.name.toLowerCase().includes(search.toLowerCase()))
                        .map(u => (
                          <button
                            key={u.id}
                            onClick={() => addMember(u.id)}
                            className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-green-50 rounded text-left text-sm transition-colors"
                            data-testid={`add-member-${u.id}`}
                          >
                            {u.image ? (
                              <img src={u.image} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-semibold shrink-0">
                                {(u.name || u.id).charAt(0).toUpperCase()}
                              </div>
                            )}
                            <span className="text-gray-800 truncate">{u.name || u.email || u.id}</span>
                          </button>
                        ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {showEdit && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="edit-channel-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <Pencil className="w-5 h-5 text-blue-600" />
              </div>
              <h4 className="font-semibold text-gray-800">Editar canal</h4>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Nombre del canal</label>
                <input
                  type="text"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="edit-channel-name"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Imagen del canal (URL)</label>
                <input
                  type="text"
                  value={editImage}
                  onChange={e => setEditImage(e.target.value)}
                  placeholder="https://..."
                  className="w-full border rounded-lg px-3 py-2 text-sm text-gray-500"
                  data-testid="edit-channel-image"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowEdit(false)}
                disabled={saving}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                data-testid="cancel-edit-btn"
              >
                Cancelar
              </button>
              <button
                onClick={saveEdit}
                disabled={!editName.trim() || saving}
                className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                data-testid="save-edit-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">{isTeamChannel ? 'Eliminar canal' : 'Eliminar chat'}</h4>
                <p className="text-sm text-gray-500">Esta accion no se puede deshacer</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              {isTeamChannel
                ? <>Se eliminara el canal <strong>#{channelName}</strong> y todo su historial de mensajes para todos los miembros.</>
                : <>Se eliminara el chat con <strong>{channelName}</strong> y todo su historial de mensajes.</>
              }
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                data-testid="cancel-delete-btn"
              >
                Cancelar
              </button>
              <button
                onClick={deleteChannel}
                disabled={deleting}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                data-testid="confirm-delete-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showTruncateConfirm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="truncate-confirm-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Borrar todos los mensajes</h4>
                <p className="text-sm text-gray-500">El canal/chat se mantiene, solo se borran los mensajes</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Se eliminaran <strong>todos los mensajes</strong> de <strong>{isTeamChannel ? `#${channelName}` : channelName}</strong> para todos los miembros. Esta accion no se puede deshacer.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowTruncateConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                data-testid="cancel-truncate-btn"
              >
                Cancelar
              </button>
              <button
                onClick={truncateChannel}
                disabled={deleting}
                className="px-4 py-2 text-sm text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                data-testid="confirm-truncate-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {deleting ? 'Borrando...' : 'Borrar mensajes'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showVideo && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black flex flex-col" data-testid="video-call-panel">
          <div className="flex items-center justify-between px-3 py-2 bg-gray-900 shrink-0">
            <div className="flex items-center gap-2 text-white min-w-0">
              <Video className="w-4 h-4 shrink-0" />
              <span className="text-sm font-medium truncate">{channelName}</span>
            </div>
            <button
              onClick={async () => {
                setShowVideo(false);
                try {
                  await channel.sendMessage({
                    text: '📵 Llamada finalizada',
                    video_call_ended: true,
                    silent: true
                  });
                } catch (e) {
                  console.warn('No se pudo enviar mensaje de llamada finalizada:', e?.message);
                }
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shrink-0 ml-2"
              data-testid="end-video-call-btn"
            >
              <X className="w-4 h-4" />
              <span className="hidden sm:inline">Volver al chat</span>
              <span className="sm:hidden">Salir</span>
            </button>
          </div>
          <iframe
            src={videoUrl}
            className="flex-1 w-full border-0"
            allow="camera *; microphone *; fullscreen; display-capture; autoplay; clipboard-write; picture-in-picture"
            allowFullScreen
            data-testid="jitsi-iframe"
          />
        </div>,
        document.body
      )}
    </div>
  );
};

// Panel for starting new direct messages
const NewChatPanel = ({ onClose, client, currentUserId, userRole }) => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await axios.get(`${API}/stream-chat/users`, { headers: headers() });
        setUsers(res.data.users || []);
      } catch (err) {
        toast.error('Error cargando usuarios');
      } finally {
        setLoading(false);
      }
    };
    loadUsers();
  }, []);

  const aiBot = { id: 'genesis_ai_bot', name: 'Genesis AI IBA', email: '', role: 'bot', image: '' };
  const canUseAI = userRole === 'admin' || userRole === 'profesor';

  const allUsers = canUseAI ? [aiBot, ...users] : users;
  const filtered = allUsers.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    (u.email && u.email.toLowerCase().includes(search.toLowerCase()))
  );

  const startChat = async (user) => {
    try {
      // Ensure user exists in Stream Chat before creating DM (skip for bot)
      if (user.email && user.role !== 'bot') {
        await axios.post(`${API}/stream-chat/ensure-user/${encodeURIComponent(user.email)}`, {}, { headers: headers() });
      }
      const channel = client.channel('messaging', {
        members: [currentUserId, user.id],
      });
      await channel.create();
      toast.success(`Chat con ${user.name} creado`);
      onClose();
    } catch (err) {
      toast.error('Error creando chat: ' + err.message);
    }
  };

  return (
    <div className="border-b bg-white" data-testid="new-chat-panel">
      <div className="p-3 flex items-center justify-between bg-blue-50">
        <span className="text-sm font-semibold text-blue-800">Nuevo Chat</span>
        <button onClick={onClose} className="p-1 hover:bg-blue-100 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Filtrar usuarios..."
            className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm"
            data-testid="search-user-input"
          />
        </div>
      </div>
      <div className="max-h-[50vh] overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></div>
        ) : filtered.length === 0 ? (
          <p className="p-4 text-center text-gray-400 text-sm">No se encontraron usuarios</p>
        ) : (
          filtered.map(u => (
            <button
              key={u.id}
              onClick={() => startChat(u)}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 text-left transition-colors"
              data-testid={`user-${u.id}`}
            >
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm shrink-0 overflow-hidden">
                {u.image ? <img src={u.image} alt="" className="w-8 h-8 rounded-full object-cover" /> : u.name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{u.name}</p>
                <p className="text-xs text-gray-400 truncate">{u.role}</p>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

// Panel for creating new channels with member selection
const NewChannelPanel = ({ onClose, client, currentUserId }) => {
  const [name, setName] = useState('');
  const [channelImage, setChannelImage] = useState('');
  const [creating, setCreating] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showMembers, setShowMembers] = useState(false);

  const getToken = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await axios.get(`${API}/stream-chat/users`, { headers: headers() });
        setUsers(res.data.users || []);
      } catch (err) {
        toast.error('Error cargando usuarios');
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);

  const toggleMember = (user) => {
    setSelectedMembers(prev =>
      prev.find(m => m.id === user.id)
        ? prev.filter(m => m.id !== user.id)
        : [...prev, user]
    );
  };

  const createChannel = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      // Ensure all selected members exist in Stream Chat
      for (const m of selectedMembers) {
        if (m.email) {
          await axios.post(`${API}/stream-chat/ensure-user/${encodeURIComponent(m.email)}`, {}, { headers: headers() });
        }
      }
      const channelId = name.trim().toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
      const memberIds = [currentUserId, ...selectedMembers.map(m => m.id)];
      const channelData = {
        name: name.trim(),
        created_by_id: currentUserId,
        members: memberIds,
      };
      if (channelImage.trim()) channelData.image = channelImage.trim();
      const channel = client.channel('team', channelId, channelData);
      await channel.create();
      toast.success(`Canal "${name}" creado con ${memberIds.length} miembros`);
      onClose();
    } catch (err) {
      toast.error('Error creando canal: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="border-b bg-white" data-testid="new-channel-panel">
      <div className="p-3 flex items-center justify-between bg-green-50">
        <span className="text-sm font-semibold text-green-800">Nuevo Canal</span>
        <button onClick={onClose} className="p-1 hover:bg-green-100 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-3 space-y-2">
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Nombre del canal"
          className="w-full border rounded-lg px-3 py-2 text-sm"
          data-testid="channel-name-input"
          autoFocus
        />

        <input
          type="text"
          value={channelImage}
          onChange={e => setChannelImage(e.target.value)}
          placeholder="URL de imagen del canal (opcional)"
          className="w-full border rounded-lg px-3 py-2 text-sm text-gray-500"
          data-testid="channel-image-input"
        />

        {/* Toggle to show/hide member selection */}
        <button
          onClick={() => setShowMembers(!showMembers)}
          className="w-full flex items-center justify-between px-3 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50"
          data-testid="toggle-members-btn"
        >
          <span className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            {selectedMembers.length > 0
              ? `${selectedMembers.length} miembro${selectedMembers.length > 1 ? 's' : ''} seleccionado${selectedMembers.length > 1 ? 's' : ''}`
              : 'Agregar miembros (opcional)'}
          </span>
          <span className="text-xs">{showMembers ? '▲' : '▼'}</span>
        </button>

        {/* Selected members tags */}
        {selectedMembers.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {selectedMembers.map(m => (
              <span key={m.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
                {m.name.split(' ')[0]}
                <button onClick={() => toggleMember(m)} className="hover:text-red-500">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Collapsible member selection */}
        {showMembers && (
          <>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar miembros..."
                className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm"
                data-testid="search-member-input"
              />
            </div>
            <div className="max-h-[40vh] overflow-y-auto border rounded-lg">
              {loadingUsers ? (
                <div className="p-3 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-gray-400" /></div>
              ) : filtered.length === 0 ? (
                <p className="p-3 text-center text-gray-400 text-xs">No se encontraron</p>
              ) : (
                filtered.map(u => {
                  const isSelected = selectedMembers.find(m => m.id === u.id);
                  return (
                    <button
                      key={u.id}
                      onClick={() => toggleMember(u)}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors ${isSelected ? 'bg-green-50' : 'hover:bg-gray-50'}`}
                      data-testid={`member-${u.id}`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300'}`}>
                        {isSelected && <span className="text-xs">&#10003;</span>}
                      </div>
                      {u.image ? (
                        <img src={u.image} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs shrink-0">{u.name.charAt(0)}</div>
                      )}
                      <span className="truncate text-gray-800">{u.name}</span>
                      <span className="text-xs text-gray-400 ml-auto shrink-0">{u.role}</span>
                    </button>
                  );
                })
              )}
            </div>
          </>
        )}

        {/* Create button - always visible */}
        <button
          onClick={createChannel}
          disabled={!name.trim() || creating}
          className="w-full py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="create-channel-btn"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Hash className="w-4 h-4" />}
          {creating ? 'Creando...' : `Crear Canal${selectedMembers.length > 0 ? ` (${selectedMembers.length + 1} miembros)` : ''}`}
        </button>
      </div>
    </div>
  );
};

export default StreamChatComponent;
